import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEdit2, FiMessageSquare } from "react-icons/fi";
import { Currency, type EvalBidDto, type EventValueDto } from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { NumericInput } from "../../../../common/input/NumericInput";

interface Props {
  bid: EvalBidDto;
  readOnly: boolean;
  onSave: (value: number | null, comment: string | undefined) => Promise<void>;
}

const formatMoney = (v: EventValueDto): string =>
  v.value != null ? `${v.value.toLocaleString("nb-NO")} ${v.currency}` : "–";

export const PriceCell = ({ bid, readOnly, onSave }: Props) => {
  const { t } = useTranslation();
  const overridden = bid.priceOverride != null;
  const effective = bid.effectivePrice;
  const supplier = bid.price;

  return (
    <Flex direction="column" align="center" gap="0.5">
      {overridden && supplier?.value != null && (
        <Text fontSize="2xs" color="smMuted" textDecoration="line-through" fontFamily="mono">
          {formatMoney(supplier)}
        </Text>
      )}
      <HStack spacing="1">
        <Text
          fontSize="2xs"
          fontFamily="mono"
          fontWeight={overridden ? "semibold" : "normal"}
          color={overridden ? "smPrimary" : "smMuted"}
        >
          {effective ? formatMoney(effective) : "–"}
        </Text>
        {bid.priceOverride?.comment && <CommentPopover comment={bid.priceOverride.comment} />}
        {!readOnly && <PriceEditPopover bid={bid} onSave={onSave} />}
      </HStack>
      {overridden && (
        <Text fontSize="2xs" color="smPrimary" textTransform="uppercase">
          {t("overridden")}
        </Text>
      )}
    </Flex>
  );
};

const CommentPopover = ({ comment }: { comment: string }) => {
  const { t } = useTranslation();
  return (
    <Popover placement="bottom" isLazy>
      <PopoverTrigger>
        <IconButton aria-label={t("Comment")} icon={<FiMessageSquare />} size="xs" variant="ghost" color="smPrimary" />
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverBody>
          <Text fontSize="sm" whiteSpace="pre-wrap" color="smText">
            {comment}
          </Text>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const PriceEditPopover = ({
  bid,
  onSave,
}: {
  bid: EvalBidDto;
  onSave: (value: number | null, comment: string | undefined) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { triggerSoft } = useApiError();
  const overridden = bid.priceOverride != null;
  const [value, setValue] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const reset = () => {
    setValue(bid.priceOverride?.value.value ?? null);
    setComment(bid.priceOverride?.comment ?? "");
  };

  const commit = async (nextValue: number | null, nextComment: string, onDone: () => void) => {
    setIsSaving(true);
    try {
      await onSave(nextValue, nextComment || undefined);
      onDone();
    } catch (e) {
      triggerSoft(e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Popover placement="bottom" isLazy onOpen={reset}>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <IconButton
              aria-label={t("Override price")}
              icon={<FiEdit2 />}
              size="xs"
              variant="ghost"
              color={overridden ? "smPrimary" : "smMuted"}
            />
          </PopoverTrigger>
          <PopoverContent w="80" maxW="90vw">
            <PopoverArrow />
            <PopoverBody>
              <Stack spacing="2">
                <Text fontSize="xs" color="smMuted" whiteSpace="normal">
                  {t("Set the price used to score this offer")}
                </Text>
                <Flex gap="1" align="center">
                  <Box flex="1">
                    <NumericInput
                      size="sm"
                      numericValue={value}
                      onNumericChange={(v) => setValue(v)}
                      placeholder={t("Price") ?? ""}
                    />
                  </Box>
                  <Text fontSize="sm" color="smMuted">
                    {Currency.Nok}
                  </Text>
                </Flex>
                <Textarea
                  size="sm"
                  rows={2}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t("Reason for the override (optional)") ?? ""}
                />
                <Flex justify="space-between" align="center">
                  {overridden ? (
                    <Button
                      size="xs"
                      variant="ghost"
                      color="smMuted"
                      isDisabled={isSaving}
                      onClick={() => commit(null, "", onClose)}
                    >
                      {t("Reset to supplier price")}
                    </Button>
                  ) : (
                    <Box />
                  )}
                  <Button
                    size="xs"
                    colorScheme="teal"
                    isLoading={isSaving}
                    onClick={() => commit(value, comment, onClose)}
                  >
                    {t("Save")}
                  </Button>
                </Flex>
              </Stack>
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
};
